import { useState } from 'react';

import { ContentCopy, PersonAdd } from '@mui/icons-material';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { ApiException, ProfileClient } from '../../api/api';

export default function InviteUserDialog({ onClose }: { onClose: () => void }) {
	const profileClient = useApiClient(ProfileClient);
	const toastService = useToastService();
	const [email, setEmail] = useState('');
	const [invitationLink, setInvitationLink] = useState('');
	const [isInviting, setIsInviting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleInvite = async () => {
		setIsInviting(true);
		setError(null);
		try {
			const invitation = await profileClient.createInvitation({ email: email.trim() });
			setInvitationLink(invitation.registrationUrl);
			toastService.success('Invitation link created');
		} catch (inviteError) {
			setError(getErrorMessage(inviteError));
		} finally {
			setIsInviting(false);
		}
	};

	const handleCopyInvitation = async () => {
		await navigator.clipboard.writeText(invitationLink);
		toastService.success('Invitation link copied');
	};

	return (
		<Dialog open onClose={isInviting ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Invite user</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{error && <Alert severity="error">{error}</Alert>}
					<TextField
						autoFocus
						label="Email"
						name="randol-invite-email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						type="email"
						autoComplete="off"
						fullWidth
					/>
					{invitationLink && (
						<TextField
							label="Invitation link"
							value={invitationLink}
							fullWidth
							InputProps={{
								readOnly: true,
								endAdornment: (
									<Tooltip title="Copy invitation link">
										<IconButton onClick={handleCopyInvitation} edge="end">
											<ContentCopy />
										</IconButton>
									</Tooltip>
								),
							}}
						/>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isInviting}>
					Close
				</Button>
				<Button variant="contained" startIcon={<PersonAdd />} onClick={handleInvite} disabled={!email.trim()} loading={isInviting}>
					Create invitation
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function getErrorMessage(error: unknown) {
	if (ApiException.isApiException(error)) {
		return error.response || error.message;
	}

	return 'The invitation request failed.';
}
